import { FieldProps } from 'formik';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Platform,
  ColorValue,
  NativeSyntheticEvent,
  StyleSheet,
  TextInput,
  TextInputKeyPressEventData,
  TextInputProps,
} from 'react-native';

import theme from '../../../../themes/theme';
import Box from '../../Box/Box';
import Text from '../../Text/Text';
import Icon from '../Icon/Icon';

interface TextFieldMultilineProps {
  autoFocus: boolean;
  label: string;
  isFullWidth?: boolean;
  isRequired?: boolean;
  placeholder?: string;
  prefix?: React.ReactChild;
  suffix?: React.ReactChild;
  textInputProps?: TextInputProps;
  isLarge?: boolean;
  showCharsRemaining?: boolean;
  borderless?: boolean;
  isLargeText?: boolean;
  marginBottom?: keyof typeof theme.spacing;
  marginTop?: keyof typeof theme.spacing;
  accessibilityLabel?: string;
  defaultValue?: string;
  validateOnChange?: boolean;
  hideError?: boolean;
  containerMaxHeight?: number;
  onChangeText?: (value?: string) => void;
  onBlur?: () => void;
  onFocus?: (b: boolean) => void;
  customBorderColor?: ColorValue;
  customBackgroundColor?: ColorValue;
  onKeyPress?: (e: NativeSyntheticEvent<TextInputKeyPressEventData>) => void;
  editable?: boolean;
  multiline?: boolean;
  height?: number;
  ref?: React.RefObject<null>;
  autoResizeHeight?: boolean;
  needToShowTailWhenSingleLine?: boolean;
  showScroll?: boolean;
}

export const TextFieldMultiline: React.FC<
  TextFieldMultilineProps & FieldProps
> = ({
  autoFocus,
  label,
  isFullWidth = true,
  isRequired,
  placeholder,
  suffix,
  prefix,
  field,
  textInputProps,
  isLarge,
  marginBottom,
  marginTop,
  showCharsRemaining,
  form: { touched, errors, setTouched, setFieldValue },
  borderless,
  isLargeText,
  accessibilityLabel,
  validateOnChange = true,
  defaultValue,
  hideError,
  onChangeText,
  onBlur,
  onFocus,
  customBorderColor,
  customBackgroundColor,
  onKeyPress,
  editable = true,
  multiline = false,
  height,
  ref,
  autoResizeHeight = false,
  needToShowTailWhenSingleLine = false,
  showScroll = true,
}) => {
  const { t } = useTranslation('forms');
  const [isFocused, setIsFocused] = useState<boolean>(false);
  const [contentHeight, setContentHeight] = useState<number>(height ?? 48);
  const shouldAutoResize = autoResizeHeight && Platform.OS === 'web';
  const MULTILINE_PADDING = showScroll ? 8 : 16;

  useEffect(() => {
    if (defaultValue) {
      setFieldValue(field.name, defaultValue);
    }
  }, []);

  const getMarginTop = () => {
    if (isLarge) return 's';
    else return marginTop ? marginTop : 'xs';
  };

  const getDynamicColors = () => {
    if (Platform.OS === 'android') {
      return {
        color: theme.colors.textPrimary,
        backgroundColor: theme.colors.white,
      };
    }
    return {};
  };

  const hasError = !!touched[field.name] && !!errors[field.name];

  return (
    <Box
      width={isFullWidth ? '100%' : undefined}
      marginBottom={isLarge ? 'l' : marginBottom || 'm'}>
      {label && (
        <Box
          flexDirection='row'
          alignItems='flex-start'
          justifyContent='space-between'
          marginBottom={isLarge ? 's' : 'xs'}>
          {!borderless && <Text variant='labelSmall'>{label}</Text>}
          {isRequired && !borderless && (
            <Text variant='metadataSecondary'>{t('required')}</Text>
          )}
        </Box>
      )}

      <Box
        flexDirection='row'
        style={
          !isLarge && {
            flex: 1,
            alignItems: 'center',
            justifyContent: 'space-between',
          }
        }>
        {!!prefix && (
          <Box
            flex={1}
            alignItems='flex-start'
            justifyContent='center'
            style={{ marginRight: -theme.spacing.xl, zIndex: 1 }}>
            {prefix}
          </Box>
        )}
        {(!needToShowTailWhenSingleLine ||
          isFocused ||
          multiline ||
          (field.value ?? '').length == 0) && (
          <TextInput
            ref={ref}
            editable={editable}
            multiline={multiline}
            style={[
              multiline && !showScroll && { overflow: 'hidden' },
              // Fixed height for multiline when not auto-resizing
              multiline &&
                !shouldAutoResize && {
                  paddingTop: theme.spacing.xxs,
                  height: height ?? 48,
                },
              borderless && { flex: 1 },
              borderless && isRequired && { minHeight: height ?? 48 },
              !borderless && {
                ...styles.input,
                fontFamily:
                  textInputProps?.secureTextEntry && Platform.OS === 'android'
                    ? undefined
                    : styles.input.fontFamily,
                ...getDynamicColors(),
              },
              // Auto-resize height for multiline on web
              // Must come after styles.input so it overrides the fixed height
              multiline &&
                shouldAutoResize && {
                  paddingTop: theme.spacing.xxs,
                  height: contentHeight,
                  minHeight: height ?? 48,
                },
              isLargeText && styles.largeText,
              !!prefix && { paddingLeft: 74 },
              !!suffix && { paddingRight: theme.spacing.xxl },

              // Use red border when there is an error
              hasError && {
                borderColor: theme.colors.alertRed,
              },

              // Custom border color overrides default when provided
              !!customBorderColor && {
                borderColor: customBorderColor,
              },

              !!customBackgroundColor && {
                backgroundColor: customBackgroundColor,
              },

              showCharsRemaining && { paddingRight: theme.spacing.xxl },

              // Multiline padding and vertical alignment
              multiline &&
                isFocused && {
                  paddingTop: MULTILINE_PADDING - (showScroll ? 0 : 4),
                  paddingBottom: MULTILINE_PADDING - (showScroll ? 0 : 4),
                  paddingLeft: MULTILINE_PADDING,
                  paddingRight: MULTILINE_PADDING + (showScroll ? 24 : 16),
                  textAlignVertical: 'top',
                },
              multiline &&
                !isFocused && {
                  paddingTop: 0,
                  paddingBottom: 0,
                  paddingLeft: 0,
                  paddingRight: 0,
                  textAlignVertical: 'top',
                },

              // Remove default browser focus outline (blue glow on web)
              {
                outlineStyle: 'none' as any,
                outlineWidth: 0,
              },

              // When focused, use black border if there is no error and no custom border
              isFocused &&
                !hasError &&
                !customBorderColor && {
                  borderColor: theme.colors.black,
                  borderRadius: theme.borderRadii.xs,
                  borderWidth: 1,
                },
            ]}
            onContentSizeChange={(event) => {
              if (multiline && shouldAutoResize) {
                setContentHeight(
                  Math.max(event.nativeEvent.contentSize.height, height ?? 48)
                );
              }
            }}
            onChangeText={(value) => {
              setTouched({ ...touched, [field.name]: validateOnChange });
              setFieldValue(field.name, value);
              onChangeText?.(value);
            }}
            onBlur={() => {
              setTouched({ ...touched, [field.name]: true });
              setIsFocused(false);
              onBlur?.();
            }}
            autoFocus={autoFocus}
            onFocus={() => {
              setIsFocused(true);
              onFocus?.(true);
            }}
            onKeyPress={onKeyPress}
            value={field.value}
            placeholder={placeholder}
            placeholderTextColor={theme.colors.grey04}
            accessibilityLabel={accessibilityLabel}
            {...textInputProps}
          />
        )}

        {needToShowTailWhenSingleLine &&
          !isFocused &&
          !multiline &&
          (field.value ?? '').length > 0 && (
            <Text
              style={[
                borderless && { flex: 1 },
                !borderless && {
                  ...styles.input,
                  paddingTop: 15,
                  ...getDynamicColors(),
                },
                isLargeText && styles.largeText,
                !!prefix && { paddingLeft: 74 },
                !!suffix && { paddingRight: theme.spacing.xxl },
                hasError && {
                  borderColor: theme.colors.alertRed,
                },
                !!customBorderColor && {
                  borderColor: customBorderColor,
                },
                !!customBackgroundColor && {
                  backgroundColor: customBackgroundColor,
                },
              ]}
              onPress={() => {
                setIsFocused(true);
              }}
              numberOfLines={1}
              ellipsizeMode='tail'
              variant='labelSmall'>
              {field.value || placeholder}
            </Text>
          )}
        {borderless && isRequired && !field.value?.length && (
          <Text variant='metadataSecondary'>{t('required')}</Text>
        )}
        {(!!suffix || showCharsRemaining) && (
          <Box
            style={{
              position: 'absolute',
              zIndex: 2,
              bottom: showScroll ? theme.spacing.xxs : theme.spacing.s,
              right: showScroll ? theme.spacing.xl : theme.spacing.m,
            }}>
            {showCharsRemaining && textInputProps?.maxLength && isFocused ? (
              <Text color='grey04' variant='webBodySecondary'>
                {textInputProps?.maxLength - (field.value?.length ?? 0)}
              </Text>
            ) : (
              suffix
            )}
          </Box>
        )}
      </Box>

      {hasError && !hideError && (
        <Box
          flexDirection='row'
          alignItems='center'
          marginTop={getMarginTop()}
          marginRight='m'>
          <Icon
            name='AlertTriangle'
            color='alertRed'
            variant='s'
            marginRight='xxs'
          />
          <Text variant='error'>{errors[field.name]}</Text>
        </Box>
      )}
    </Box>
  );
};

const styles = StyleSheet.create({
  input: {
    width: '100%',
    paddingHorizontal: theme.spacing.m,
    borderWidth: 1,
    borderRadius: theme.spacing.xs,
    borderColor: theme.colors.grey02,
    backgroundColor: theme.colors.white,
    fontFamily: 'Inter_400Regular',
    height: 48,
  },
  largeText: {
    ...theme.textVariants.labelLarge,
    color: theme.colors.textPrimary,
  },
});
